import { usePaginationWithComponent } from 'hooks/usePaginationWithComponent';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getRequest, postRequest } from 'services/api';
import { LIMIT_DATA_PER_PAGE } from 'utils/constants';
import { buildQueryString } from 'utils/queryHelpers';

// Membuat context untuk loading
const InvoiceHistoryContext = createContext();

// Provider untuk context loading
export const InvoiceHistoryProvider = ({ children }) => {
    const { id } = useParams();
    const [invoices, setInvoices] = useState({
        data: [],
        pagination: null,
        error: null,
        loading: false,
        pageLoading: false,
    });

    const [filterOptions, setFilterOptions] = useState({
        search: '',
        currentPage: 1,
        totalPages: 1,
    });

    const queryString = buildQueryString({
        search: filterOptions.search || '',
        page: filterOptions.currentPage,
        limit: LIMIT_DATA_PER_PAGE,
        ...(id && { 'filter[invoice_id]': id }),
    });


    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            console.log('search', e.target.value);
            setFilterOptions((prevState) => ({
                ...prevState,
                search: e.target.value,
            }));
        }
    };

    const fetchInvoices = useCallback(async () => {
        setInvoices((prevState) => ({
            ...prevState,
            loading: true,
        }));

        try {
            const response = await getRequest(`/invoice-history${queryString}`);
            if (response.success) {
                setInvoices({
                    data: response.data,
                    pagination: response.pagination,
                    error: null,
                    loading: false,
                });

                setFilterOptions((prev) => ({
                    ...prev,
                    totalPages: response.pagination.last_page
                }));

                return { success: true, message: 'Success', data: response.data };
            }
        } catch (error) {
            setInvoices({
                data: [],
                pagination: null,
                error: error.message,
                loading: false,
            });
            return { success: false, message: 'Failed to fetch invoices' };
        }
    }, [queryString, id]);

    useEffect(() => {
        setInvoices((prevState) => ({
            ...prevState,
            pageLoading: true,
        }));
        fetchInvoices().then(() => {
            setInvoices((prevState) => ({
                ...prevState,
                pageLoading: false,
            }));
        });
    }, [queryString, id]);

    const handlePageChange = (page) => {
        setFilterOptions((prevState) => ({
            ...prevState,
            currentPage: page,
        }));
    };

    const totalPages = invoices.pagination?.last_page || 1;

    const Pagination = usePaginationWithComponent({
        totalPages: totalPages,
        currentPage: filterOptions.currentPage,
        onPageChange: handlePageChange,
        visiblePages: 5, // Optional: number of visible pages in pagination (default is 5)
    });

    const generateInvoice = async (invoice_id) => {
        setInvoices((prevState) => ({
            ...prevState,
            loading: true,
        }));

        try {
            const response = await postRequest(`/invoice/${invoice_id}/generate`);
            if (response.success) {
                return { success: true, message: 'Invoice has been generated' };
            }
        } catch (error) {
            return { success: false, message: 'Failed to generate invoices' };
        } finally {
            setInvoices((prevState) => ({
                ...prevState,
                loading: false,
            }));
        }
    };

    const handleSentWhatsapp = async (invoice_id) => {
        setInvoices((prevState) => ({
            ...prevState,
            pageLoading: true,
        }));
        try {
            const response = await postRequest(`/invoice/${invoice_id}/send-whatsapp`);
            if (response.success) {
                setInvoices((prevState) => ({
                    ...prevState,
                    pageLoading: false,
                }));
                return { success: true, message: 'Invoice has been sent' };
            }
        } catch (error) {
            setInvoices((prevState) => ({
                ...prevState,
                pageLoading: false,
            }));
            return { success: false, message: 'Failed to send invoice' };
        }
    };

    const handleSentEmail = async (invoice_id) => {
        setInvoices((prevState) => ({
            ...prevState,
            pageLoading: true,
        }));
        try {
            const response = await postRequest(`/invoice/${invoice_id}/send-email`);
            if (response.success) {
                setInvoices((prevState) => ({
                    ...prevState,
                    pageLoading: false,
                }));
                return { success: true, message: 'Invoice has been sent' };
            }
        } catch (error) {
            setInvoices((prevState) => ({
                ...prevState,
                pageLoading: false,
            }));
            return { success: false, message: 'Failed to send invoice' };
        }
    };

    return (
        <InvoiceHistoryContext.Provider value={{ invoices, handleSearch, filterOptions, Pagination, refetch: fetchInvoices, generateInvoice, id, handleSentWhatsapp, handleSentEmail }}>
            {children}
        </InvoiceHistoryContext.Provider>
    );
};

// Hook untuk mengakses context loading
export const useInvoiceHistory = () => {
    const context = useContext(InvoiceHistoryContext);
    if (!context) {
        throw new Error('useInvoiceHistory must be used within a InvoicehistoryProvider');
    }
    return context;
};